import type { StateCreator } from 'zustand';
import type { StoreState, Lore } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

export interface LoreState {
    entries: Lore[];
}

export interface LoreActions {
    addLore: (lore: Omit<Lore, 'id'>) => Promise<void>;
    updateLore: (lore: Partial<Lore> & { id: number }) => Promise<void>;
    deleteLore: (id: number) => Promise<void>;
}

export type LoreSlice = LoreState & LoreActions;

export const createLoreActions: StateCreator<StoreState, [['zustand/immer', never]], [], { state: LoreState, actions: LoreActions }> = (set, get) => ({
    state: {
        entries: [],
    },
    actions: {
        addLore: async (lore) => {
            const tempId = -Date.now();
            const user = get().auth.user;
            const tempLore = { 
                ...lore, 
                id: tempId, 
                userId: user?.id, 
                user: { username: user?.username } 
            };
            set(state => { state.session.lore.entries.push(tempLore as Lore); });

            try {
                const response = await api('/api/lore', { method: 'POST', body: JSON.stringify(lore) });
                const newLore = await response.json() as Lore;
                set(state => {
                    const index = (state.session.lore.entries ?? []).findIndex((l: Lore) => l.id === tempId);
                    if (index !== -1) state.session.lore.entries[index] = newLore;
                });
            } catch (error) {
                logger.error(error, 'Error adding lore, rolling back.');
                set(state => { state.session.lore.entries = (state.session.lore.entries ?? []).filter((l: Lore) => l.id !== tempId); });
                throw error;
            }
        },
        updateLore: async (lore) => {
            const originalLore = get().session.lore.entries;
            const loreIndex = (originalLore ?? []).findIndex(l => l.id === lore.id);
            if (loreIndex === -1) return;

            set(state => { state.session.lore.entries[loreIndex] = { ...state.session.lore.entries[loreIndex]!, ...lore }; });

            try {
                await api(`/api/lore/${lore.id}`, { method: 'PUT', body: JSON.stringify(lore) });
            } catch (error) {
                logger.error(error, `Error updating lore ${lore.id}, rolling back.`);
                set(state => { state.session.lore.entries = originalLore; });
                throw error;
            }
        },
        deleteLore: async (id) => {
            const originalLore = get().session.lore.entries;
            set(state => { state.session.lore.entries = (state.session.lore.entries ?? []).filter((l: Lore) => l.id !== id); });

            try {
                await api(`/api/lore/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting lore ${id}, rolling back.`);
                set(state => { state.session.lore.entries = originalLore; });
                throw error;
            }
        },
    }
});